import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Car,
  Download,
  FileText,
  Fuel,
  Gauge,
  Link2Off,
  Pencil,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate, formatDateTime } from '@/lib/format';
import { extractErrorMessage } from '@/lib/api-client';
import { useVehicle } from './api';
import { VehicleFormDialog } from './vehicle-form-dialog';
import { FUEL_TYPE_LABELS, VEHICLE_CATEGORY_LABELS } from './types';
import { useVehicleDocuments, useCreateVehicleDocument, useDeleteVehicleDocument, getUploadDownloadUrl } from '@/features/documents/api';
import { VEHICLE_DOCUMENT_LABELS, formatFileSize } from '@/features/documents/types';
import { UploadDropzone } from '@/features/uploads/upload-dropzone';
import { useAssignmentsByVehicle, useEndAssignment } from '@/features/assignments/api';
import { AssignmentFormDialog } from '@/features/assignments/assignment-form-dialog';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}

export function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const vehicleQuery = useVehicle(id ?? null);
  const docsQuery = useVehicleDocuments(id ?? '', !!id);
  const assignmentsQuery = useAssignmentsByVehicle(id ?? '', !!id);
  const createDoc = useCreateVehicleDocument(id ?? '');
  const deleteDoc = useDeleteVehicleDocument(id ?? '');

  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const endAssignment = useEndAssignment();

  const v = vehicleQuery.data;

  if (vehicleQuery.isLoading) {
    return (
      <div className="page space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!v) {
    return (
      <div className="page">
        <p className="text-sm text-muted-foreground">Veículo não encontrado.</p>
        <Button variant="outline" onClick={() => navigate('/app/vehicles')}>
          <ArrowLeft className="size-4" /> Voltar
        </Button>
      </div>
    );
  }

  const docs = docsQuery.data?.data ?? [];
  const assignments = assignmentsQuery.data?.data ?? [];

  return (
    <div className="page">
      {/* Header */}
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/vehicles')}>
            <ArrowLeft className="size-5" />
          </Button>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h1 className="font-mono text-2xl font-bold tracking-wide md:text-3xl">{v.plate}</h1>
              {v.active ? (
                <Badge variant="success">Ativo</Badge>
              ) : (
                <Badge variant="outline">Inativo</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {v.brand} {v.model} {v.year ? `· ${v.year}` : ''}
              {v.color ? ` · ${v.color}` : ''}
            </p>
          </div>
        </div>
        <Button onClick={() => setEditOpen(true)}>
          <Pencil className="size-4" /> Editar
        </Button>
      </header>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">
            <Car className="size-4" /> Visão geral
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="size-4" /> Documentos ({docs.length})
          </TabsTrigger>
          <TabsTrigger value="drivers">
            <Users className="size-4" /> Motoristas ({assignments.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab: Visão geral */}
        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Car className="size-4" /> Identificação
                </CardTitle>
              </CardHeader>
              <CardContent className="divide-y divide-border">
                <InfoRow label="Placa" value={v.plate} />
                <InfoRow label="Marca" value={v.brand} />
                <InfoRow label="Modelo" value={v.model} />
                <InfoRow label="Ano" value={v.year} />
                <InfoRow label="Cor" value={v.color} />
                <InfoRow label="Renavam" value={v.renavam} />
                <InfoRow label="Chassi" value={v.chassi} />
                <InfoRow
                  label="Categoria"
                  value={
                    <Badge variant="secondary">
                      {VEHICLE_CATEGORY_LABELS[v.category]}
                    </Badge>
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Gauge className="size-4" /> Operação
                </CardTitle>
              </CardHeader>
              <CardContent className="divide-y divide-border">
                <InfoRow
                  label="Combustível"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <Fuel className="size-3.5 text-muted-foreground" />
                      {FUEL_TYPE_LABELS[v.fuelType]}
                    </span>
                  }
                />
                <InfoRow
                  label="Odômetro"
                  value={`${(v.odometerMeters / 1000).toLocaleString('pt-BR')} km`}
                />
                {v.tracksHourmeter && (
                  <InfoRow
                    label="Horímetro"
                    value={`${(v.hourmeterMinutes / 60).toFixed(1)} h`}
                  />
                )}
                <InfoRow
                  label="Tanque"
                  value={v.tankCapacityMl ? `${(v.tankCapacityMl / 1000).toFixed(1)} L` : null}
                />
                <Separator />
                <InfoRow label="CRLV" value={v.crlvNumber} />
                <InfoRow label="Vencimento CRLV" value={formatDate(v.crlvExpiresAt)} />
                <InfoRow label="RNTRC (ANTT)" value={v.anttRntrc} />
                <Separator />
                <InfoRow label="Cadastrado em" value={formatDateTime(v.createdAt)} />
                <InfoRow label="Última atualização" value={formatDateTime(v.updatedAt)} />
                <InfoRow label="Versão" value={v.version} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Documentos */}
        <TabsContent value="documents">
          <div className="space-y-4">
            <UploadDropzone
              purpose="vehicle_document"
              label="Envie CRLV, seguro, licenciamento ou outros documentos"
              onUploaded={async (uploaded) => {
                try {
                  await createDoc.mutateAsync({
                    type: 'other',
                    description: uploaded.filename,
                    uploadId: uploaded.uploadId,
                  });
                  toast.success('Documento adicionado');
                } catch (err) {
                  toast.error(extractErrorMessage(err));
                }
              }}
            />

            {docs.length === 0 && !docsQuery.isLoading && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhum documento cadastrado ainda.
              </p>
            )}

            {docs.map((doc) => (
              <Card key={doc.id}>
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <FileText className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {VEHICLE_DOCUMENT_LABELS[doc.type as keyof typeof VEHICLE_DOCUMENT_LABELS] ?? doc.type}
                        {doc.description && ` — ${doc.description}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {doc.upload.filename} · {formatFileSize(doc.upload.sizeBytes)}
                        {doc.expiresAt && (
                          <>
                            {' · '}
                            <Calendar className="inline size-3" /> Vence{' '}
                            {formatDate(doc.expiresAt)}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={async () => {
                        const url = await getUploadDownloadUrl(doc.upload.id);
                        window.open(url, '_blank');
                      }}
                    >
                      <Download className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive"
                      onClick={async () => {
                        await deleteDoc.mutateAsync(doc.id);
                        toast.success('Documento removido');
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab: Motoristas (assignments) */}
        <TabsContent value="drivers">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setAssignOpen(true)}>
                <Plus className="size-4" />
                Vincular motorista
              </Button>
            </div>

            {assignments.length === 0 && !assignmentsQuery.isLoading && (
              <Card>
                <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
                  <Users className="size-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Nenhum motorista vinculado</p>
                  <p className="text-xs text-muted-foreground">
                    Clique em "Vincular motorista" para associar um motorista a este veículo.
                  </p>
                </CardContent>
              </Card>
            )}

            {assignments.map((a) => (
              <Card key={a.id}>
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                      {a.driver.name
                        .split(' ')
                        .map((p) => p[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{a.driver.name}</p>
                      <p className="text-xs text-muted-foreground">
                        CNH {a.driver.cnhCategory} · {a.driver.cpf}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right text-xs text-muted-foreground">
                      <p>Desde {formatDate(a.startedAt)}</p>
                      {a.endedAt ? (
                        <Badge variant="outline">Encerrado {formatDate(a.endedAt)}</Badge>
                      ) : (
                        <Badge variant="success">Ativo</Badge>
                      )}
                    </div>
                    {!a.endedAt && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive"
                        title="Encerrar vínculo"
                        onClick={async () => {
                          try {
                            await endAssignment.mutateAsync({ id: a.id });
                            toast.success('Vínculo encerrado');
                          } catch (err) {
                            toast.error(extractErrorMessage(err));
                          }
                        }}
                      >
                        <Link2Off className="size-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <VehicleFormDialog open={editOpen} onOpenChange={setEditOpen} vehicle={v} />
      {v && (
        <AssignmentFormDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          mode={{
            kind: 'forVehicle',
            vehicleId: v.id,
            vehicleLabel: `${v.plate}${v.brand || v.model ? ` (${[v.brand, v.model].filter(Boolean).join(' ')})` : ''}`,
          }}
        />
      )}
    </div>
  );
}
